import React, { useEffect, useMemo, useState } from "react"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { toast } from "react-toastify"
import { marketingTopTracks } from "../../../../apis"
import { errorFilledToastOptions } from "../../../../constants"
import { mapListToCamelCase } from "../../../../utility"
import {
  MarketingTopTrack,
  MarketingTopTrackOptions,
  MarketingTopTrackPeriod
} from "../../../../types"
import Loading from "../../../ui/Loading"
import NotFound from "../../../NotFound"

const PERIODS: { label: string; value: MarketingTopTrackPeriod }[] = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" }
]

const formatNumber = (value?: number | null) =>
  value === null || value === undefined ? "-" : value.toLocaleString()

const formatStreamChange = (value?: number | null) => {
  if (value === null || value === undefined || value === 0) return "-"
  const sign = value > 0 ? "+" : ""
  return `${sign}${value.toLocaleString()}`
}

const formatPercent = (value?: number | null) =>
  value === null || value === undefined ? "-" : `${value.toFixed(1)}%`

const MarketingTopTracksTab = () => {
  const [period, setPeriod] = useState<MarketingTopTrackPeriod>("daily")
  const [country, setCountry] = useState<string>("")
  const [date, setDate] = useState<string>("")

  const [rows, setRows] = useState<MarketingTopTrack[]>([])
  const [options, setOptions] = useState<MarketingTopTrackOptions>({
    countryOptions: [],
    dateOptions: []
  })
  const [loading, setLoading] = useState(false)

  const query = useMemo(() => {
    const params = new URLSearchParams()
    params.set("period", period)
    if (country) params.set("country", country)
    if (date) params.set("date", date)
    return `/?${params.toString()}`
  }, [period, country, date])

  const fetchData = () => {
    setLoading(true)
    marketingTopTracks(query, "get")
      .then((resp) => {
        const results = mapListToCamelCase(resp.data?.results || []) as MarketingTopTrack[]
        const opts = resp.data?.options || {}
        setRows(results)
        setOptions({
          countryOptions: opts.country_options || [],
          dateOptions: opts.date_options || []
        })
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        toast.error(err?.response?.data?.detail || "Error fetching top tracks", errorFilledToastOptions)
      })
  }

  useEffect(() => {
    fetchData()
  }, [period, country, date])

  const groupedRows = useMemo(() => {
    const groups: { [key: string]: MarketingTopTrack[] } = {}
    rows.forEach((row) => {
      const key = row.country
      if (!groups[key]) groups[key] = []
      groups[key].push(row)
    })
    Object.values(groups).forEach((list) => list.sort((a, b) => a.position - b.position))
    return groups
  }, [rows])

  // Aggregate the same track (ISRC) across all loaded countries. Computed
  // directly here — no backend record — summing streams and deriving each
  // track's share (%) of the combined total.
  const totalAggregate = useMemo(() => {
    if (rows.length === 0) return null

    const byIsrc: { [isrc: string]: MarketingTopTrack } = {}
    const countrySet = new Set<string>()

    rows.forEach((row) => {
      if (row.country) countrySet.add(row.country)
      const existing = byIsrc[row.isrc]
      if (existing) {
        existing.streams = (existing.streams || 0) + (row.streams || 0)
        existing.streamChange = (existing.streamChange || 0) + (row.streamChange || 0)
        if (!existing.trackTitle && row.trackTitle) existing.trackTitle = row.trackTitle
        if ((!existing.artists || existing.artists.length === 0) && row.artists) existing.artists = row.artists
      } else {
        byIsrc[row.isrc] = {
          ...row,
          streams: row.streams || 0,
          streamChange: row.streamChange || 0
        }
      }
    })

    const aggregated = Object.values(byIsrc)
      .sort((a, b) => (b.streams || 0) - (a.streams || 0))
      .slice(0, 100)
    const totalStreams = aggregated.reduce((sum, r) => sum + (r.streams || 0), 0)
    const list = aggregated.map((row, index) => ({
      ...row,
      position: index + 1,
      sharePct: totalStreams > 0 ? ((row.streams || 0) / totalStreams) * 100 : 0
    }))
    const countries = Array.from(countrySet).sort()

    return { list, countries, totalStreams, date: rows[0]?.date ?? "-" }
  }, [rows])

  return (
    <div className="p-6">
      {loading && <Loading />}

      <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          {PERIODS.map((option) => (
            <button
              key={option.value}
              className={`h-10 px-4 text-sm ${
                period === option.value ? "bg-[#111] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setPeriod(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <select
          className="h-10 border border-gray-200 rounded-lg px-3 text-sm"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        >
          <option value="">All countries</option>
          {options.countryOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          className="h-10 border border-gray-200 rounded-lg px-3 text-sm"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        >
          <option value="">Latest date</option>
          {options.dateOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

      </div>

      {!loading && rows.length === 0 && (
        <div className="flex w-full h-full items-center justify-center pt-[100px]">
          <NotFound
            title="No top tracks yet"
            description="Country top 100 tables fill after the weekly cron runs. Make sure the marketing top-100 scheduler has countries configured."
            imageWidth={250}
          />
        </div>
      )}

      {totalAggregate && (
        <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <KTitleSpan
                text={`Total (${totalAggregate.countries.join(", ") || "-"})`}
                fontSize={16}
                color="#111"
                lineHeight="24px"
                fontWeight={600}
              />
              <KSpan
                text={`Period: ${period} · date: ${totalAggregate.date} · ${totalAggregate.list.length} tracks · ${formatNumber(
                  totalAggregate.totalStreams
                )} streams`}
                color="#666"
                fontSize={12}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase">
                  <th className="py-2 pr-4">#</th>
                  <th className="py-2 pr-4">Track</th>
                  <th className="py-2 pr-4">Artist(s)</th>
                  <th className="py-2 pr-4">Streams</th>
                  <th className="py-2 pr-4">Share</th>
                  <th className="py-2 pr-4">Change</th>
                  <th className="py-2 pr-4">ISRC</th>
                </tr>
              </thead>
              <tbody>
                {totalAggregate.list.map((row) => (
                  <tr key={`total-${row.isrc}`} className="border-t border-gray-100">
                    <td className="py-2 pr-4 text-gray-500">{row.position}</td>
                    <td className="py-2 pr-4 text-gray-800">{row.trackTitle || "-"}</td>
                    <td className="py-2 pr-4 text-gray-700">
                      {(row.artists || []).map((a) => a.name).join(", ") || "-"}
                    </td>
                    <td className="py-2 pr-4 text-gray-800">{formatNumber(row.streams)}</td>
                    <td className="py-2 pr-4 text-gray-700">{formatPercent(row.sharePct)}</td>
                    <td
                      className={`py-2 pr-4 ${
                        (row.streamChange || 0) > 0
                          ? "text-emerald-600"
                          : (row.streamChange || 0) < 0
                          ? "text-red-600"
                          : "text-gray-500"
                      }`}
                    >
                      {formatStreamChange(row.streamChange)}
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{row.isrc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {Object.keys(groupedRows).length > 0 && (
        <div className="grid gap-4">
          {Object.entries(groupedRows).map(([groupCountry, list]) => {
            return (
              <div key={groupCountry} className="bg-white border border-gray-100 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <KTitleSpan
                      text={`Kahuna ${groupCountry} Top ${list.length}`}
                      fontSize={16}
                      color="#111"
                      lineHeight="24px"
                      fontWeight={600}
                    />
                    <KSpan
                      text={`Period: ${period} · date: ${list[0]?.date ?? "-"}`}
                      color="#666"
                      fontSize={12}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 uppercase">
                        <th className="py-2 pr-4">#</th>
                        <th className="py-2 pr-4">Track</th>
                        <th className="py-2 pr-4">Artist(s)</th>
                        <th className="py-2 pr-4">Streams</th>
                        <th className="py-2 pr-4">Change</th>
                        <th className="py-2 pr-4">ISRC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((row) => (
                        <tr key={`${row.isrc}-${row.position}`} className="border-t border-gray-100">
                          <td className="py-2 pr-4 text-gray-500">{row.position}</td>
                          <td className="py-2 pr-4 text-gray-800">{row.trackTitle || "-"}</td>
                          <td className="py-2 pr-4 text-gray-700">
                            {(row.artists || []).map((a) => a.name).join(", ") || "-"}
                          </td>
                          <td className="py-2 pr-4 text-gray-800">{formatNumber(row.streams)}</td>
                          <td
                            className={`py-2 pr-4 ${
                              (row.streamChange || 0) > 0
                                ? "text-emerald-600"
                                : (row.streamChange || 0) < 0
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {formatStreamChange(row.streamChange)}
                          </td>
                          <td className="py-2 pr-4 text-gray-500">{row.isrc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MarketingTopTracksTab
